/* eslint jsx-a11y/no-noninteractive-element-interactions: 0 */
/* eslint jsx-a11y/no-noninteractive-tabindex: 0 */

import { type ReactNode, createContext, useContext, useMemo, useRef, useState } from 'react';
import { rightSidebarShowActionIcons, sidebarMetricInstrumentation } from '@gonfalon/dogfood-flags/dogfood-flags';
import { IconButton, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { trackSidebarResized } from '../internal/analytics';
import { getKeyboardShortcutLabel, KeyboardShortcut } from '../internal/keyboardShortcuts';

import { useProjectEntitySidebarContext } from './useProjectEntitySidebarContext';

import styles from './ResizableSidebarLayout.module.css';

function clamp(x: number, min: number, max: number) {
  return Math.max(min, Math.min(max, x));
}

type SidebarConfiguration = {
  initial: number;
  min: number;
  max: number;
};

type ResizableSidebarLayoutState = {
  sidebarConfig: SidebarConfiguration;
  width: number;
  isDragging: boolean;
  setWidth: (width: number) => void;
  setDragging: (isDragging: boolean) => void;
};

const ResizableSidebarLayoutContext = createContext<ResizableSidebarLayoutState | undefined>(undefined);

export function useResizableSidebarLayoutState({
  initialSidebarWidth,
  minimumSidebarWidth,
  maximumSidebarWidth,
}: {
  minimumSidebarWidth: number;
  maximumSidebarWidth: number;
  initialSidebarWidth: number;
}) {
  const [width, setWidth] = useState(initialSidebarWidth);
  const [isDragging, setDragging] = useState(false);

  return useMemo(
    () => ({
      width,
      isDragging,
      setWidth,
      setDragging,
      sidebarConfig: { initial: initialSidebarWidth, min: minimumSidebarWidth, max: maximumSidebarWidth },
    }),
    [width, initialSidebarWidth, isDragging, setDragging, minimumSidebarWidth, maximumSidebarWidth],
  );
}

/**
 * A layout that supports a resizable sidebar.
 *
 * NOTE: at the moment this only works for users with pointer devices (e.g. mouse, finger, etc).
 */
export function ResizableSidebarLayout({
  state,
  children,
}: {
  state: ResizableSidebarLayoutState;
  children: ReactNode;
}) {
  return (
    <ResizableSidebarLayoutContext.Provider value={state}>
      <div
        className={styles.layout}
        style={{
          // @ts-expect-error --resizable-sidebar-size is not a known css property
          '--resizable-sidebar-size': `${String(state.width)}px`,
        }}
      >
        {children}
      </div>
    </ResizableSidebarLayoutContext.Provider>
  );
}

export function ResizeHandle() {
  const layout = useResizableSidebarLayoutContext();
  const sidebar = useProjectEntitySidebarContext();
  const showCollapsibleSidebar = rightSidebarShowActionIcons();

  // originalWidth doubles as the drag-origin width: it's set on pointer down and only reassigned
  // mid-drag when the sidebar is re-opened by dragging. For a genuine resize (sidebar stays
  // visible) it holds the starting width, so we compare against it on pointer up to fire a single
  // Sidebar Resized event with the final width.
  const originalWidth = useRef(layout.width);
  const originalClientX = useRef(layout.width);

  return (
    <div
      role="separator"
      tabIndex={0}
      className={styles.resizeHandle}
      data-dragging={layout.isDragging ? 'true' : undefined}
      data-collapsed={!sidebar.isVisible ? 'true' : undefined}
      onDragStart={() => false}
      onTouchStart={(event) => event.preventDefault()}
      onTouchMove={(event) => event.preventDefault()}
      onTouchCancel={(event) => event.preventDefault()}
      onTouchEnd={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        document.body.style.userSelect = 'none';
        document.body.style.touchAction = 'none';
        originalWidth.current = layout.width;
        originalClientX.current = event.clientX;
        layout.setDragging(true);
      }}
      onPointerMove={(event) => {
        event.preventDefault();

        if (!layout.isDragging) {
          return;
        }

        const distanceToViewportEdge = document.documentElement.clientWidth - event.clientX;
        if (sidebar.isVisible && distanceToViewportEdge < layout.sidebarConfig.min / 2) {
          sidebar.toggle({ trigger: 'resize-drag', width: layout.width });
        } else if (!sidebar.isVisible && distanceToViewportEdge > layout.sidebarConfig.min) {
          // Use the freshly computed width (not layout.width, which is the stale pre-setWidth
          // value) when reporting the reopen.
          const reopenWidth = Math.floor(
            clamp(distanceToViewportEdge, layout.sidebarConfig.min, layout.sidebarConfig.max),
          );
          layout.setWidth(reopenWidth);
          originalWidth.current = distanceToViewportEdge;
          originalClientX.current = event.clientX;
          sidebar.toggle({ trigger: 'resize-drag', width: reopenWidth });
        }

        const delta = event.clientX - originalClientX.current;

        // We subtract the delta because the sidebar is on the right side of the screen
        const nextWidth = Math.floor(
          clamp(originalWidth.current - delta, layout.sidebarConfig.min, layout.sidebarConfig.max),
        );

        layout.setWidth(nextWidth);
      }}
      onPointerUp={() => {
        document.body.style.userSelect = '';
        document.body.style.touchAction = '';
        layout.setDragging(false);
        if (sidebar.isVisible && layout.width !== originalWidth.current && sidebarMetricInstrumentation()) {
          trackSidebarResized({ width: layout.width, page: window.location.pathname, sidebar: 'project-entity' });
        }
      }}
      onPointerCancel={() => {
        document.body.style.userSelect = '';
        document.body.style.touchAction = '';
        layout.setDragging(false);
      }}
    >
      {!showCollapsibleSidebar && (
        <TooltipTrigger>
          <IconButton
            className={styles.toggle}
            variant="minimal"
            size="small"
            aria-label={sidebar.isVisible ? 'Hide sidebar' : 'Show sidebar'}
            icon={sidebar.isVisible ? 'sidebar-left-collapse' : 'sidebar-left-expand'}
            onPress={() => sidebar.toggle({ trigger: 'button', width: layout.width })}
            data-test-id="sidebar-toggle"
          />
          <Tooltip placement="bottom">
            <span className={styles.tooltip}>
              {sidebar.isVisible ? (
                <>
                  Hide sidebar<kbd>{getKeyboardShortcutLabel(KeyboardShortcut.TOGGLE_PROJECT_ENTITY_SIDEBAR)}</kbd>
                </>
              ) : (
                <>
                  Show sidebar<kbd>{getKeyboardShortcutLabel(KeyboardShortcut.TOGGLE_PROJECT_ENTITY_SIDEBAR)}</kbd>
                </>
              )}
            </span>
          </Tooltip>
        </TooltipTrigger>
      )}
    </div>
  );
}

export function useResizableSidebarLayoutContext() {
  const context = useContext(ResizableSidebarLayoutContext);
  if (!context) {
    throw new Error('useResizableSidebarLayoutContext must be used within a ResizableSidebarLayoutProvider');
  }
  return context;
}
